import React, { useState } from "react";
import CustomerLedger from "@/pages/customer/CustomerLedger";
import CustomerMonthlySummary from "@/pages/customer/MonthlySummary";
import CustomerRejections from "@/pages/customer/CustomerRejections";
import ReconciliationStatement from "@/pages/customer/ReconciliationStatement";
import { Receipt, CalendarBlank, XCircle, ArrowsCounterClockwise } from "@phosphor-icons/react";

/**
 * v1.27.67 — Customer Reports hub with three internal sub-tabs.
 * v1.27.69 — Added "Rejections" sub-tab for admin remarks on rejected slips.
 * v1.28.12 — Added "Reconciliation Statement" sub-tab with date-range filter
 * + downloadable PDF. All screens rendered inline; existing endpoints reused.
 */
export default function CustomerReports() {
    const [tab, setTab] = useState("ledger");
    const tabs = [
        { id: "ledger",         label: "My Ledger",              icon: Receipt,               testId: "customer-reports-tab-ledger" },
        { id: "reconciliation", label: "Reconciliation Statement", icon: ArrowsCounterClockwise, testId: "customer-reports-tab-reconciliation" },
        { id: "monthly",        label: "Monthly Summary",        icon: CalendarBlank,         testId: "customer-reports-tab-monthly" },
        { id: "rejections",     label: "Rejections",             icon: XCircle,               testId: "customer-reports-tab-rejections" },
    ];
    return (
        <div className="space-y-5" data-testid="customer-reports-page">
            <div className="flex flex-col gap-2">
                <div className="text-xs uppercase tracking-[0.2em] text-slate-500">Portal</div>
                <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">Reports</h1>
                <p className="text-sm text-slate-500">Your ledger, monthly totals and slip rejection remarks in one place.</p>
            </div>

            <div
                role="tablist"
                aria-label="Reports section"
                data-testid="customer-reports-tabs"
                className="inline-flex flex-wrap rounded-full border border-slate-200 bg-white p-1 shadow-sm"
            >
                {tabs.map((t) => {
                    const Icon = t.icon;
                    const active = tab === t.id;
                    return (
                        <button
                            key={t.id}
                            type="button"
                            role="tab"
                            data-testid={t.testId}
                            aria-selected={active}
                            onClick={() => setTab(t.id)}
                            className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${
                                active
                                    ? (t.id === "rejections" ? "bg-rose-600 text-white shadow"
                                        : t.id === "reconciliation" ? "bg-indigo-700 text-white shadow"
                                        : "bg-emerald-700 text-white shadow")
                                    : "text-slate-600 hover:text-slate-900"
                            }`}
                        >
                            <Icon size={16} weight={active ? "fill" : "regular"} />
                            {t.label}
                        </button>
                    );
                })}
            </div>

            <div className="pt-1">
                {tab === "ledger" && <CustomerLedger />}
                {tab === "reconciliation" && <ReconciliationStatement />}
                {tab === "monthly" && <CustomerMonthlySummary />}
                {tab === "rejections" && <CustomerRejections />}
            </div>
        </div>
    );
}
